import { useCallback, useEffect, useState } from "react";
import { ArticleCard } from "../components/ArticleCard";
import { PageState } from "../components/PageState";
import { getArticles, getCachedArticles, type PostSummary } from "../lib/articles";
import { usePageMetadata } from "../lib/metadata";

export function ArticlesPage() {
  const [articles, setArticles] = useState<PostSummary[] | null>(() => getCachedArticles());
  const [cached, setCached] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  usePageMetadata({
    title: "Inbox cleanup guides — Emails Unsubscriber",
    description: "Practical explanations of Gmail unsubscribe links, bulk inbox cleanup, spam controls, and email subscription management.",
    path: "/articles",
  });

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const savedArticles = getCachedArticles();
    if (savedArticles) setArticles(savedArticles);
    setCached(false);
    setError(null);
    getArticles(controller.signal)
      .then((result) => {
        setArticles(result.data);
        setCached(result.cached);
      })
      .catch(() => {
        if (!controller.signal.aborted && !savedArticles) setError("unavailable");
      });
    return () => controller.abort();
  }, [attempt]);

  if (error) {
    return <PageState title="The guides are temporarily unavailable." detail="Try again in a moment, or open Emails Unsubscriber to start cleaning up your inbox." action={retry} />;
  }

  return (
    <section className="unsub-guides">
      <div className="unsub-shell unsub-guides-shell">
        <div className="unsub-guides-heading">
          <p className="unsub-eyebrow">Inbox field notes</p>
          <h1>Clear answers for a quieter inbox.</h1>
          <p>How unsubscribe links work, why some senders make you confirm, and practical ways to clean up Gmail without opening every message.</p>
          {cached ? <p className="unsub-cache-note" role="status">Showing the most recently saved guide list.</p> : null}
        </div>

        <div className="unsub-guide-list">
          {articles === null ? (
            <div className="unsub-guide-loading" aria-label="Loading guides">
              <span /><span /><span />
            </div>
          ) : articles.length ? (
            articles.map((article, index) => <ArticleCard key={article.slug} article={article} index={index} />)
          ) : (
            <div className="unsub-guide-empty"><strong>No guides have been published yet.</strong><span>The writer has not produced its first approved article.</span></div>
          )}
        </div>
      </div>
    </section>
  );
}
